import React from "react"
import Layout from "../../components/layout"
import SubNavigation from "../../components/sub-navigation"
import ReadNext from "../../components/read-next"
import BarChart from "../../components/bar-chart"
import { TabGroup } from "../../components/tab-group"
import Hero from "../../components/hero"
import List from "../../components/list"
import Accordion from "../../components/accordion"
import Stat from "../../components/stat"
import IconHands from "../../images/capacity-development/hands.inline.svg"
import IconHand from "../../images/capacity-development/hand.inline.svg"
import IconDiploma from "../../images/capacity-development/diploma.inline.svg"
import IconOfficials from "../../images/capacity-development/officials.inline.svg"
import IconUmbrella from "../../images/capacity-development/umbrella.inline.svg"
import IconLanguages from "../../images/capacity-development/languages.inline.svg"
import { AppendixWaypoint, Appendix } from "../../components/appendix"
import CDSpending from "../../images/capacity-development/cd_spending.inline.svg"

const PAGESLUG = "capacity-development"

export default function CapacityDevelopment() {
  return (
    <Layout>
      <SubNavigation id="What We Do" current={PAGESLUG} />
      <Hero
        image="capacity-development/hero-capacity-development.jpg"
        title="Capacity Development"
        text="Strengthening the capacity of institutions, including central banks, finance ministries, revenue administrations, statistical agencies, and financial sector supervisory agencies, results in more effective policies and greater economic stability and inclusion. The IMF works with countries to strengthen these institutions by providing technical assistance and training focused on issues that are critical to economic stability and growth."
        widthLarge="true"
      />
      <section className="page__section">
        <div className="page__section__inner">
          <div className="container container--large">
            <h2 className="page__title">Building Institutions</h2>
            <p>
              The IMF provides capacity development—hands-on technical
              assistance, policy-oriented training, and peer-learning
              opportunities—so countries can build sustainable and resilient
              institutions that can weather external shocks. These efforts are
              an important contribution to countries’ progress toward the
              Sustainable Development Goals.
            </p>
            <p>
              Capacity development (CD) focuses on the IMF’s core areas of
              expertise and helps countries tackle cross-cutting issues, such as
              income inequality, gender equality, corruption, and climate
              change. The IMF is uniquely positioned to support its membership
              with its global reach, institutional experience, and world-class
              expertise. All countries benefit from capacity development, with
              more support and better tailoring of capacity development
              activities for fragile states.
            </p>
            <p>
              At the request of country authorities, IMF country teams and
              technical experts develop and implement an integrated work plan.
              The IMF typically works with countries through a global network of
              regional centers, in-country placements of long-term resident
              advisors, short-term visits by IMF staff and experts, classroom
              training, and free online courses. In response to the COVID-19
              pandemic, IMF capacity development teams quickly pivoted to
              support institutions in formulating their economic policy
              response, leveraging technology and innovative modalities to
              ensure continued engagement when countries needed it most.
            </p>
          </div>
        </div>
      </section>
      <section className="page__section page__section--border-top--small">
        <div className="page__section__inner">
          <div className="container container--full">
            <ul className="columns columns--three">
              <li>
                <div className="stat-mega">
                  <div className="stat-mega__value">
                    <IconHand />
                    <Stat number={305} />
                    <div className="stat-mega__unit">Million</div>
                  </div>
                  <p>
                    for <b>hands-on technical advice</b>, policy-oriented
                    training, and peer learning
                  </p>
                </div>
              </li>
              <li>
                <div className="stat-mega">
                  <div className="stat-mega__value">
                    <IconHands />
                    <Stat number={2838} />
                  </div>
                  <p>
                    <b>technical assistance</b> visits involving 1,617 experts
                  </p>
                </div>
              </li>
              <li>
                <div className="stat-mega">
                  <div className="stat-mega__value">
                    <IconUmbrella />
                    <Stat number={3} />
                  </div>
                  <p>
                    <b>fragile states</b> among the top 10 recipients of
                    technical assistance
                  </p>
                </div>
              </li>
            </ul>
            <ul className="columns columns--three">
              <li>
                <div className="stat-mega">
                  <div className="stat-mega__value">
                    <IconDiploma />
                    <Stat number={383} />
                  </div>
                  <p>
                    <b>courses delivered</b>
                  </p>
                </div>
              </li>
              <li>
                <div className="stat-mega">
                  <div className="stat-mega__value">
                    <IconLanguages />
                    <Stat number={7} />
                  </div>
                  <p>
                    <b>languages offered</b>
                  </p>
                </div>
              </li>
              <li>
                <div className="stat-mega">
                  <div className="stat-mega__value">
                    <IconOfficials />
                    <Stat number={15542} />
                  </div>
                  <p>
                    <b>officials trained</b>
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>
      <section className="page__section page__section--border-top--small">
        <div className="page__section__inner">
          <div className="container container--full">
            <ul className="columns columns--three">
              <li>
                <List id="cd-partners-for-imf" listType="ol" />
              </li>
              <li>
                <List id="cd-recipients-spending" listType="ol" />
              </li>
              <li>
                <List id="cd-recipients-participation" listType="ol" />
              </li>
            </ul>
          </div>
        </div>
      </section>
      <section className="page__section page__section--border-top--small">
        <div className="page__section__inner">
          <div className="container container--full">
            <ul className="columns columns--two">
              <li>
                <h3 className="title--tertiary title--uppercase">
                  Capacity Development Spending as Share of Major IMF Activities
                </h3>
                <figure
                  style={{ maxWidth: "350px", margin: "10px auto 0 auto" }}
                  aria-label="29% Capacity Development (comprised of 24% Technical Assistance and 5% Training), 23% Bilateral Surveillance, 19% Multilateral Surveillance, 15% Lending, 11% Oversight of Global Systems, and 3% Miscellaneous."
                >
                  <div
                    className="scaling-svg-container"
                    style={{
                      paddingBottom: "100%",
                    }}
                  >
                    <CDSpending />
                  </div>
                </figure>
              </li>
              <li>
                <header className="section-header">
                  <div className="section-header__text">
                    <h2 className="section-header__title">
                      Spending on Capacity Development
                      <br />
                      FY 2016–20
                    </h2>
                    <div className="section-header__subtitle">
                      (Millions of US Dollars, by Fiscal Year)
                    </div>
                  </div>
                </header>
                <BarChart
                  id="spending-on-capacity-development"
                  measure="million dollars"
                />
              </li>
            </ul>
          </div>
        </div>
      </section>
      <section className="page__section page__section--border-top--small">
        <div className="page__section__inner">
          <div className="container container--full">
            <TabGroup
              controls={["Region", "Income Group", "Topic"]}
              title="Direct Capacity Development Delivery FY 2016–20"
              subtitle="(Percent Share of Total, by Fiscal Year)"
            >
              <>
                <ul className="swipe-view swipe-view--columns--five">
                  <li>
                    <BarChart
                      id="capacity-africa"
                      subTitle="Sub-Saharan africa"
                      measure="percent"
                    />
                  </li>
                  <li>
                    <BarChart
                      id="capacity-asia"
                      subTitle="Asia and Pacific"
                      measure="percent"
                    />
                  </li>
                  <li>
                    <BarChart
                      id="capacity-europe"
                      subTitle="Europe"
                      measure="percent"
                    />
                  </li>
                  <li>
                    <BarChart
                      id="capacity-middle-east"
                      subTitle="Middle East and Central Asia"
                      measure="percent"
                    />
                  </li>
                  <li>
                    <BarChart
                      id="capacity-western-hemisphere"
                      subTitle="Western Hemisphere"
                      measure="percent"
                    />
                  </li>
                </ul>
              </>
              <>
                <ul className="swipe-view swipe-view--columns--three">
                  <li>
                    <BarChart
                      id="capacity-income-advanced"
                      subTitle="Advanced Economies"
                      measure="percent"
                    />
                  </li>
                  <li>
                    <BarChart
                      id="capacity-income-emerging"
                      subTitle="Emerging market and middle-income economies"
                      measure="percent"
                    />
                  </li>
                  <li>
                    <BarChart
                      id="capacity-income-low"
                      subTitle="Low-income developing countries"
                      measure="percent"
                    />
                  </li>
                </ul>
              </>
              <>
                <ul className="swipe-view swipe-view--columns--five">
                  <li>
                    <BarChart
                      id="capacity-topic-public"
                      subTitle="Public Finances"
                      measure="percent"
                    />
                  </li>
                  <li>
                    <BarChart
                      id="capacity-topic-monetary"
                      subTitle="Monetary and Financial Systems"
                      measure="percent"
                    />
                  </li>
                  <li>
                    <BarChart
                      id="capacity-topic-statistics"
                      subTitle="Statistics"
                      measure="percent"
                    />
                  </li>
                  <li>
                    <BarChart
                      id="capacity-topic-legal"
                      subTitle="Legal Frameworks"
                      measure="percent"
                    />
                  </li>
                  <li>
                    <BarChart
                      id="capacity-topic-macroeconomic"
                      subTitle="Macroeconomic Frameworks"
                      measure="percent"
                    />
                  </li>
                </ul>
              </>
            </TabGroup>
          </div>
        </div>
      </section>
      <section className="page__section page__section--border-top--small">
        <div className="page__section__inner">
          <div className="container container--full">
            <TabGroup
              controls={["Region", "Income Group"]}
              title="Total Training Participation FY 2016–20"
              subtitle="(Number of Participants, by Fiscal Year)"
            >
              <>
                <ul className="swipe-view swipe-view--columns--five">
                  <li>
                    <BarChart
                      id="training-africa"
                      subTitle="Sub-Saharan africa"
                      measure="participants"
                    />
                  </li>
                  <li>
                    <BarChart
                      id="training-asia"
                      subTitle="Asia and Pacific"
                      measure="participants"
                    />
                  </li>
                  <li>
                    <BarChart
                      id="training-europe"
                      subTitle="Europe"
                      measure="participants"
                    />
                  </li>
                  <li>
                    <BarChart
                      id="training-middle-east"
                      subTitle="Middle East and Central Asia"
                      measure="participants"
                    />
                  </li>
                  <li>
                    <BarChart
                      id="training-western-hemisphere"
                      subTitle="Western Hemisphere"
                      measure="participants"
                    />
                  </li>
                </ul>
              </>
              <>
                <ul className="swipe-view swipe-view--columns--three">
                  <li>
                    <BarChart
                      id="training-advanced"
                      subTitle="Advanced Economies"
                      measure="participants"
                    />
                  </li>
                  <li>
                    <BarChart
                      id="training-emerging"
                      subTitle="Emerging market and middle-income economies"
                      measure="participants"
                    />
                  </li>
                  <li>
                    <BarChart
                      id="training-low-income"
                      subTitle="Low-income developing countries"
                      measure="participants"
                    />
                  </li>
                </ul>
              </>
            </TabGroup>
          </div>
        </div>
      </section>
      <section className="page__section page__section--border-top--small">
        <div className="page__section__inner">
          <div className="container container--large">
            <AppendixWaypoint page={PAGESLUG} id={1} />
            <Accordion id="thematic-country-funds" />
          </div>
        </div>
      </section>
      <section className="page__section">
        <div className="page__section__inner">
          <div className="container container--large">
            <AppendixWaypoint page={PAGESLUG} id={2} />
            <Accordion id="regional-capacity-development-centers" />
          </div>
        </div>
      </section>
      <ReadNext sectionID="who-we-are" />
      <Appendix page={PAGESLUG} />
    </Layout>
  )
}
